import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators'
import {Session} from '@/rest/models';
import { SessionApi } from '@/rest';
import { TextCanvasConfig } from '@/components/common/TextCanvas.vue'
import { text } from '@fortawesome/fontawesome-svg-core';
import { store } from '@/store'

export class Step {
    label: string
    active: boolean
    completed: boolean
    componentName: string
    optional: boolean
    usageName?: string
    clipRects?: [[number, number], [number, number], [number, number], [number, number]?]
    info?: string
    textConfig?: TextCanvasConfig
    imageEditorPlaceholderStyle?: string
  
    constructor(label: string, componentName: string, optional: boolean, usageName?: string, clipRects?: [[number, number], [number, number], 
            [number, number], [number, number]?], info?: string, textConfig?: TextCanvasConfig, imageEditorPlaceholderStyle?: string) {
        this.label = label
        this.componentName = componentName
        this.usageName = usageName
        this.active = false
        this.completed = false
        this.optional = optional
        this.info = info
        if (clipRects) {
            if(clipRects.length > 3 && clipRects[3]) {
                this.clipRects = [[clipRects[0][0], clipRects[0][1]], [clipRects[1][0], clipRects[1][1]], [clipRects[2][0], clipRects[2][1]], [clipRects[3][0], clipRects[3][1]]]
            }else {
                this.clipRects = [[clipRects[0][0], clipRects[0][1]], [clipRects[1][0], clipRects[1][1]], [clipRects[2][0], clipRects[2][1]]]
            }
        }
        this.textConfig = textConfig
        this.imageEditorPlaceholderStyle = imageEditorPlaceholderStyle
    }

}

export interface AppState {
    session: Session
    steps: Step[]
    currentStepIndex: number
    showPreview: boolean
    isClean: boolean
    showGiftOptions: boolean
    showShippingOverlay: boolean
    productID: string
    productVariant: string
    hidePreviewButton: boolean
}

@Module({ dynamic: true, store, name: "app"})
export class App extends VuexModule implements AppState {

    public session: Session = {}
    public isClean: boolean = true
    public steps: Step[] = []
    public currentStepIndex = 0
    public showPreview = false
    public showShippingOverlay = false
    public showGiftOptions = false
    public productID: string = ""
    public productVariant: string = ""
    public hidePreviewButton: boolean = false

    @Mutation
    public SET_SESSION(session: Session){
        this.session = session
    }

    @Mutation
    private SET_ACTIVE_STEP(activeStep: number) {
        if (this.currentStepIndex !== activeStep) {
            this.steps[this.currentStepIndex].active = false
            this.currentStepIndex = activeStep
        }
        let step = this.steps[this.currentStepIndex]
        if (!step.active) {
            step.active = true
        }
    }

    @Mutation
    private DEACTIVATE_CURRENT_STEP() {
        let step = this.steps[this.currentStepIndex]
        if (step.active) {
            step.active = false
        }
    }

    @Mutation
    private COMPLETE_CURRENT_STEP() {
        let step = this.steps[this.currentStepIndex]
        if (!step.completed) {
            step.completed = true
            this.isClean = false
        }
    }

    @Mutation
    private COMPLETE_STEP(index: number) {
        let step = this.steps[index]
        if (!step.completed) {
            step.completed = true
            this.isClean = false
        }
    }

    @Mutation
    private SET_SHOW_PREVIEW(showPreview: boolean) {
        this.showPreview = showPreview
    }

    @Mutation
    private SET_SHOW_SHIPPING_OVERLAY(showShippingOverlay: boolean) {
        this.showShippingOverlay = showShippingOverlay
    }

    @Mutation
    private SET_IS_CLEAN(isClean: boolean){
        this.isClean = isClean
    }

    @Mutation
    public SET_STEPS(steps: Step[]){
        this.steps = steps
    }

    @Mutation
    private SET_SHOW_GIFT_OPTIONS(showGiftOptions: boolean) {
        this.showGiftOptions = showGiftOptions
    }

    @Mutation
    public SET_CUSTOM_PUZZLE_CLIPPER(ratio: number[]){
        let clipper = this.steps[this.currentStepIndex].clipRects
        if (clipper != undefined){
            clipper[0] = [ratio[0], ratio[1]]
        }
    }

    @Mutation
    private SET_PRODUCT_ID(productID: string) {
        this.productID = productID
    }

    @Mutation
    private SET_PRODUCT_Variant(productVariant: string) {
        this.productVariant = productVariant
    }

    @Mutation
    private SET_HIDE_PREVIEW_BUTTON(hidePreviewButton: boolean) {
        this.hidePreviewButton = hidePreviewButton
    }

    @Mutation
    private RESET_STEPS(reset: boolean) {
        this.steps.forEach(step => {
            if (step.completed) {
                step.completed = false
            }
            if (step.active) {
                step.active = false
            }
        })
    }

    @Mutation
    public SET_TEXT_CONFIG(textConfig: TextCanvasConfig) {
        let config = this.steps[3].textConfig
        if (config !== undefined){
            this.steps[3].textConfig = textConfig
        }
    }

    @Action({ commit: 'SET_SESSION' })
    public SetSession(session: Session) {
        return session
    }

    @Action({ commit: 'DEACTIVATE_CURRENT_STEP' })
    public SetShowMenu() {}

    @Action({ commit: 'SET_ACTIVE_STEP' })
    public SetActiveStep(activeStep: number) {
        return activeStep
    }

    @Action({ commit: 'COMPLETE_CURRENT_STEP' })
    public CompleteCurrentStep() {}

    @Action({ commit: 'COMPLETE_STEP' })
    public CompleteStep(index: number) {
        return index
    }

    @Action({ commit: 'SET_SHOW_PREVIEW' })
    public SetShowPreview(showPreview: boolean) {
        return showPreview
    }

    @Action({ commit: 'SET_SHOW_SHIPPING_OVERLAY' })
    public SetShowShippingOverlay(showShippingOverlay: boolean) {
        return showShippingOverlay
    }

    @Action({ commit: 'SET_IS_CLEAN' })
    public SetIsClean(isClean: boolean) {
        return isClean
    }

    @Action({ commit: 'SET_STEPS' })
    public SetSteps(steps: Step[]) {
        return steps
    }

    @Action({ commit: 'SET_SHOW_GIFT_OPTIONS' })
    public SetShowGiftOptions(showGiftOptions: boolean) {
        return showGiftOptions
    }
    @Action({ commit: 'SET_CUSTOM_PUZZLE_CLIPPER' })
    public SetCustomPuzzleClipper(ratio: number[]){
        return ratio
    }

    @Action({ commit: 'SET_PRODUCT_ID' })
    public SetProductID(productID: string) {
        return productID
    }

    @Action({ commit: 'SET_PRODUCT_Variant' })
    public SetProductVariant(productVariant: string) {
        return productVariant
    }
    
    @Action({ commit: 'SET_HIDE_PREVIEW_BUTTON' })
    public SetHidePreviewButton(hidePreviewButton: boolean) {
        return hidePreviewButton
    }

    @Action({ commit: 'RESET_STEPS' })
    public ResetSteps(reset: boolean) {
        return reset
    }

    @Action({ commit: 'SET_TEXT_CONFIG' })
    public SetTextConfig(textConfig: TextCanvasConfig){
        return textConfig
    }

    get allStepsCompleted(): boolean {
        return this.steps.every(step => step.completed || step.optional)
    }

    get activeStepComponent(): string | undefined {
        let step = this.steps[this.currentStepIndex]
        return step.active ? step.componentName : undefined
    }

    get showMenu(): boolean {
        let step = this.steps[this.currentStepIndex]
        return !step.active
    }

    get stepOptional(): boolean {
        let step = this.steps[this.currentStepIndex]
        return step.optional
    }

    get textConfig(): TextCanvasConfig | undefined {
        let step = this.steps[this.currentStepIndex]
        return step.textConfig
    }

    get currentStepImagePlaceholderStyle(): string {
        let step = this.steps[this.currentStepIndex]
        if(step.imageEditorPlaceholderStyle) {
            return step.imageEditorPlaceholderStyle
        }
        return ""
    }

    get hasGiftOptions(): boolean {
        if (this.session.productType) {
            return this.session.productType === 'HERZBUCH'
        }
        return false
    }

    get hasDynamicInfoPosition(): boolean {
        if (this.session.productType) {
            return this.session.productType !== 'HERZBUCH'
        }
        return false
    }

    get currentStepIndexGetter(): number {
        return this.currentStepIndex
    }

    get isLastStep(): boolean {
        return this.steps.length - 1 === this.currentStepIndex
    }
}

export const AppModule = getModule(App)